import React, { useState, useEffect, useRef, useCallback } from 'react';

const EFFECT_POOL_SIZE = 8;
const CLICK_FLUSH_MS = 750;
const MAX_TAPS_PER_BATCH = 250;

function createRequestId() {
  if (crypto.randomUUID) return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const ClickerPage = ({
  triggerIncomeAnimation,
  realuserID,
  username,
  clickCount,
  setClickCount,
  clickPower,
  setClickPower,
  fetchUserData,
  setRank,
  maxClicks,
  setRankProgress,
  energy,
  setEnergy,
  maxEnergy,
  dailyCooldown,
  claimDailyReward,
  refs,
  getRank,
  income,
  totalClicksPerHour,
  setCurrentPage, 
  triggerToast,
  clan_id,
  notifications, 
  setNotifications,
  TotalClickBonus,
}) => {
  const [clanBonus, setClanBonus] = useState(0); // Состояние для хранения бонуса
  const [showMenu, setShowMenu] = React.useState(false);
  const pendingTapsRef = useRef(0);
  const inFlightBatchRef = useRef(null);
  const retryBatchRef = useRef(null);
  const flushTimerRef = useRef(null);
  const effectsLayerRef = useRef(null);
  const nextEffectRef = useRef(0);
  const renderFrameRef = useRef(null);
  const pendingRenderRef = useRef({ clicks: 0, energy: 0 });
  const lastVibrationRef = useRef(0);
  
const energyRef = useRef(energy);
const clickCountRef = useRef(clickCount);
useEffect(() => {
  energyRef.current = energy;
}, [energy]);

useEffect(() => {
  clickCountRef.current = clickCount;
}, [clickCount]);


  const calculateReferralBonus = (refs) => {
    return Math.floor(refs / 10) * 10; 
  };

  const referralBonus = calculateReferralBonus(refs);


    // Получаем топ-100 кланов
    useEffect(() => {
      const fetchTopClans = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/clans/top/fulltop`);
          if (!response.ok) throw new Error("Ошибка загрузки топа кланов");
          const data = await response.json();
  
          // Находим текущий клан пользователя в топе
          const userClan = data.find((clan) => clan.id === clan_id);
          if (userClan) {
            const clanPosition = data.findIndex((clan) => clan.id === clan_id) + 1; // Позиция клана
            
            // Логика бонуса (пример)
            const calculatedBonus = Math.max(100 - (clanPosition - 1) * 20, 0);
          
            setClanBonus(calculatedBonus);
          }
          
        } catch (error) {
          console.error('Ошибка при загрузке топ-100 кланов:', error);
        }
      };
  
      if (clan_id) fetchTopClans();
    }, [clan_id]);


  const triggerVibration = () => {
    const now = performance.now();
    if (navigator.vibrate && now - lastVibrationRef.current > 80) {
      navigator.vibrate(10);
      lastVibrationRef.current = now;
    }
  };


  const handleClaimReward = async (notificationId,realuserID, amount) => {
    try {
      // Отправка запроса на получение награды
      const userId = realuserID;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/leaderboard/claim`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ notificationId, userId, amount }),
      });

      if (response.ok) {
        // Обновляем состояние, удаляя забранное уведомление
        setNotifications((prevNotifications) =>
          prevNotifications.filter((notification) => notification.id !== notificationId)
        );
        triggerToast(`Вы получили ${amount} кликов!`);
        setClickCount(Number(clickCount) + (Number(amount) || 0));
      } else {
        triggerToast("Ошибка при получении награды");
      }
    } catch (error) {
      console.error("Ошибка при забирании награды:", error);
    }
  };

  const renderClickEffect = useCallback((x, y, amount) => {
    const nodes = effectsLayerRef.current?.children;
    if (!nodes?.length) return;
    const node = nodes[nextEffectRef.current++ % nodes.length];
    node.textContent = `+${amount}`;
    node.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    node.classList.remove('click-effect-active');
    void node.offsetWidth;
    node.classList.add('click-effect-active');
  }, []);

  const commitVisualBatch = useCallback(() => {
    renderFrameRef.current = null;
    const batch = pendingRenderRef.current;
    pendingRenderRef.current = { clicks: 0, energy: 0 };
    if (batch.clicks) setClickCount((value) => Number(value) + (Number(batch.clicks) || 0));
    if (batch.energy) setEnergy((value) => Math.max(0, value - batch.energy));
  }, [setClickCount, setEnergy]);

  const sendBufferedClicks = useCallback(async () => {
    if (inFlightBatchRef.current || (!retryBatchRef.current && pendingTapsRef.current <= 0) || !username) return;
    const batch = retryBatchRef.current || (() => {
      const tapCount = Math.min(pendingTapsRef.current, MAX_TAPS_PER_BATCH);
      pendingTapsRef.current -= tapCount;
      return {
        requestId: createRequestId(),
        tapCount,
        username,
      };
    })();
    inFlightBatchRef.current = batch;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/update_clicks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-Init-Data': window.Telegram?.WebApp?.initData || '',
          ...(process.env.NODE_ENV === 'development' ? { 'X-Dev-Username': username } : {}),
        },
        body: JSON.stringify(batch),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      retryBatchRef.current = null;
      inFlightBatchRef.current = null;
      if (Number.isFinite(Number(result.clickCount))) setClickCount(Number(result.clickCount));
      if (Number.isFinite(Number(result.energy))) {
        energyRef.current = Number(result.energy);
        setEnergy(Number(result.energy));
      }
      if (pendingTapsRef.current > 0) queueMicrotask(sendBufferedClicks);
    } catch (error) {
      // Preserve the request id: if the server committed before the connection
      // failed, retrying this exact batch is safe and cannot credit it twice.
      retryBatchRef.current = batch;
      inFlightBatchRef.current = null;
      console.error('Ошибка пакетной отправки кликов:', error);
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(sendBufferedClicks, 2000);
    }
  }, [setClickCount, setEnergy, username]);

  const scheduleFlush = useCallback(() => {
    clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(sendBufferedClicks, CLICK_FLUSH_MS);
  }, [sendBufferedClicks]);

  const handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    if (energyRef.current <= 0) return;
    const bonusMultiplier = 1 + referralBonus / 100;
    const bonusMultiplierClan = 1 + clanBonus / 100;
    const clicksWithBonus = Math.floor(
      clickPower * bonusMultiplier * bonusMultiplierClan + TotalClickBonus
    );
    energyRef.current -= 1;
    clickCountRef.current += clicksWithBonus;
    pendingTapsRef.current += 1;
    pendingRenderRef.current.clicks += clicksWithBonus;
    pendingRenderRef.current.energy += 1;
    if (!renderFrameRef.current) {
      renderFrameRef.current = requestAnimationFrame(commitVisualBatch);
    }
    renderClickEffect(event.clientX, event.clientY, clicksWithBonus);
    triggerVibration();
    scheduleFlush();
  };

  useEffect(() => {
    const flushWhenHidden = () => {
      if (document.visibilityState === 'hidden') sendBufferedClicks();
    };
    document.addEventListener('visibilitychange', flushWhenHidden);
    return () => {
      document.removeEventListener('visibilitychange', flushWhenHidden);
      clearTimeout(flushTimerRef.current);
      if (renderFrameRef.current) cancelAnimationFrame(renderFrameRef.current);
      sendBufferedClicks();
    };
  }, [sendBufferedClicks]);
  
// Получение данных пользователя и обновление ранга
useEffect(() => {
  const updateRankProgress = () => {
    const newRank = getRank(clickCount);
    if (!newRank || !Number(maxClicks[newRank])) return;
    setRank(newRank);
    const progress = Math.min((Number(clickCount) / Number(maxClicks[newRank])) * 100, 100);
    setRankProgress(progress);
  };

  updateRankProgress();
}, [clickCount, getRank, maxClicks, setRank, setRankProgress]);


  return (
    <div
    className="container"
    onPointerDown={handlePointerDown}
  >
    <div ref={effectsLayerRef} className="click-effects-layer" aria-hidden="true">
      {Array.from({ length: EFFECT_POOL_SIZE }, (_, index) => (
        <span className="click-effect-pooled" key={index} />
      ))}
    </div>
        <div className="bonuses-container">
  <div className="bonus-block">
    <span className="bonus-title">Рефералка</span>
    <span className="bonus-value">{referralBonus}%</span>
  </div>
  <div className="bonus-block">
    <span className="bonus-title">Клан</span>
    <span className="bonus-value">+{clanBonus}%</span>
  </div>
  <div className="bonus-block">
    <span className="bonus-title">Предметы</span>
    <span className="bonus-value">+{TotalClickBonus}%</span>
  </div>
</div>
    <div className="Coin_Main" >
      <img src="/assets/currency-coin-v2.png" alt="" />
      {clickCount.toLocaleString()}
    </div>

    <div className="Subfooter">
  <div className="Row_Container">
    <div className="Energy_Block">
      Энергия
      <div className="Energy_Slider">
        <div
          className="Energy_Fill"
          style={{ width: `${(energy / maxEnergy) * 100}%` }}
        />
        <span className="Energy_Text">
          {energy}/{maxEnergy}
        </span>
      </div>
    </div>

    <div
  className={`Notification_Block ${notifications?.length > 0 ? 'pulse' : ''}`}
  onClick={() => setShowMenu(true)}
>
  🔔
</div>

  </div>


  {showMenu && (
  <div className="Notification_Menu">
    <div className="Notification_Content">
      <span className="notification-header">Ваши уведомления</span>
      {notifications.length > 0 ? (
        <ul className="notification-list">
          {notifications.map((notification) => (
            <li key={notification.id} className="notification-item">
              <div className="notification-title">{notification.title}</div>
              <div className="notification-description">
                {notification.description || "Вы получили награду!"}
              </div>
              <div className="notification-amount">
                +{notification.amount.toLocaleString()} кликов
              </div>
              {notification.type === "ref" && (
                <button
                  className="claim-reward-btn"
                  onClick={() =>
                    handleClaimReward(notification.id, realuserID, notification.amount)
                  }
                >
                  Забрать
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="notification-empty">Нет новых уведомлений</p>
      )}
      <button className="buttonCLS" onClick={() => setShowMenu(false)}>
        Закрыть
      </button>
    </div>
  </div>
)}

</div>

  </div>
  );
};

export default ClickerPage;
